import { useEffect, useState } from "react";
import { X, Sparkles, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { RestoreLogo } from "@/components/RestoreMark";

const STORAGE_KEY = "restore.welcome.v1";
const DELAY_MS = 4000; // wait 4s after arrival before opening

export default function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", sms_opt_in: false });

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen) return;
      const t = setTimeout(() => setOpen(true), DELAY_MS);
      return () => clearTimeout(t);
    } catch { /* localStorage disabled — silent */ }
  }, []);

  const dismiss = (subscribed = false) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ subscribed, at: Date.now() }));
    } catch {}
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/marketing/subscribe", { ...form, source: "welcome_popup" });
      setDone(true);
      // Auto-close after showing success state briefly
      setTimeout(() => dismiss(true), 2200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      data-testid="welcome-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={() => dismiss(false)}
        className="absolute inset-0 bg-sage-800/50 backdrop-blur-sm animate-[fade-up_0.4s_ease]"
        data-testid="welcome-backdrop"
      />

      {/* Panel */}
      <div className="relative w-full sm:max-w-3xl bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(20,25,20,0.35)] grid sm:grid-cols-5 animate-[fade-up_0.55s_cubic-bezier(0.22,1,0.36,1)]">
        {/* Left visual */}
        <div className="hidden sm:block sm:col-span-2 relative bg-sage-800">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(230,201,127,0.28),transparent_60%)]" />
          <div className="relative h-full p-8 flex flex-col justify-between text-white">
            <RestoreLogo shimmer className="h-24 self-start" data-testid="welcome-logo" />
            <div>
              <div className="overline text-clay-400 mb-3">Welcome to Restore</div>
              <div className="font-display text-3xl leading-tight">
                A little something<br />before you go.
              </div>
            </div>
          </div>
        </div>

        {/* Right form / success */}
        <div className="sm:col-span-3 p-7 sm:p-10 relative">
          <div className="sm:hidden flex justify-center mb-5"><RestoreLogo shimmer className="h-16" data-testid="welcome-logo-mobile" /></div>
          <button
            type="button"
            onClick={() => dismiss(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center text-sage-800/60 hover:text-sage-800 hover:bg-sand-100 transition-colors"
            aria-label="Close"
            data-testid="welcome-close"
          >
            <X className="w-4 h-4" />
          </button>

          {!done ? (
            <>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-clay-600">
                <Sparkles className="w-3.5 h-3.5" /> Members save more
              </div>
              <h2 id="welcome-title" className="font-display text-3xl sm:text-4xl text-sage-800 mt-3 leading-tight">
                Get 10% off your first order.
              </h2>
              <p className="mt-3 text-sage-800/65 text-sm leading-relaxed">
                Join the Restore list for wellness tips, IV specials, and member-only offers. Unsubscribe anytime.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="welcome-form">
                <div>
                  <Label htmlFor="w-name" className="text-xs uppercase tracking-widest text-sage-800/60">First name</Label>
                  <Input
                    id="w-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 rounded-xl h-11 border-sand-300 focus-visible:ring-sage-700/20"
                    placeholder="Jane"
                    data-testid="welcome-name"
                  />
                </div>
                <div>
                  <Label htmlFor="w-email" className="text-xs uppercase tracking-widest text-sage-800/60">Email *</Label>
                  <Input
                    id="w-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 rounded-xl h-11 border-sand-300 focus-visible:ring-sage-700/20"
                    placeholder="jane@example.com"
                    data-testid="welcome-email"
                  />
                </div>
                <div>
                  <Label htmlFor="w-phone" className="text-xs uppercase tracking-widest text-sage-800/60">Mobile *</Label>
                  <Input
                    id="w-phone"
                    type="tel"
                    required
                    minLength={7}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1.5 rounded-xl h-11 border-sand-300 focus-visible:ring-sage-700/20"
                    placeholder="(956) 555-0100"
                    data-testid="welcome-phone"
                  />
                </div>

                <label className="flex items-start gap-2.5 pt-2 cursor-pointer">
                  <Checkbox
                    id="w-sms"
                    checked={form.sms_opt_in}
                    onCheckedChange={(v) => setForm({ ...form, sms_opt_in: !!v })}
                    className="mt-0.5 border-sage-700/40 data-[state=checked]:bg-sage-700 data-[state=checked]:border-sage-700"
                    data-testid="welcome-sms-optin"
                  />
                  <span className="text-xs text-sage-800/70 leading-relaxed">
                    Yes, text me occasional wellness tips & IV specials. Standard message rates may apply. Reply STOP to opt out.
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={submitting || !form.email || !form.phone || form.phone.trim().length < 7}
                  className="btn-pill h-12 w-full mt-3 bg-sage-700 hover:bg-sage-800 text-white text-sm shadow-none"
                  data-testid="welcome-submit"
                >
                  {submitting ? "Sending…" : "Send my 10% off code"}
                </Button>

                <button
                  type="button"
                  onClick={() => dismiss(false)}
                  className="w-full text-center text-xs text-sage-800/50 hover:text-sage-800/80 pt-1"
                  data-testid="welcome-skip"
                >
                  No thanks, I'll pass
                </button>
              </form>
            </>
          ) : (
            <div className="py-8 text-center" data-testid="welcome-success">
              <div className="w-14 h-14 rounded-full bg-sage-700 text-white grid place-items-center mx-auto">
                <Check className="w-6 h-6" strokeWidth={2.4} />
              </div>
              <h3 className="font-display text-3xl text-sage-800 mt-5">You're in.</h3>
              <p className="mt-3 text-sage-800/70 text-sm">
                Check your inbox for your 10% off code. Welcome to Restore.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
